import { isEqual } from 'lodash';

/**
 * Type guard to check if an object is of type `StackInfo`.
 *
 * This function checks if the provided object contains the necessary properties
 * (`mac_address`, `stack_serial_number`, and `boards`) and ensures `boards` is an array.
 *
 * @param obj - The object to check.
 * @returns `true` if the object conforms to the `StackInfo` type, otherwise `false`.
 */
export function isStackInfo(obj: any): obj is StackInfo {
  return (
    obj && typeof obj === 'object' && 'mac_address' in obj && 'stack_serial_number' in obj && Array.isArray(obj.boards)
  );
}

/**
 * Type guard to check if an object is of type `HardwareDescription`.
 *
 * This function validates if the provided object contains the necessary properties,
 * including `fileVersion`, a `device` object with `serialNumber`, and optionally an `assembly` property.
 *
 * @param obj - The object to check.
 * @returns `true` if the object conforms to the `HardwareDescription` type, otherwise `false`.
 */
export function isHardwareDescription(obj: any): obj is HardwareDescription {
  return (
    obj &&
    typeof obj === 'object' &&
    'fileVersion' in obj &&
    typeof obj.fileVersion === 'string' &&
    ('assembly' in obj || 'device' in obj) &&
    'serialNumber' in obj.device &&
    typeof obj.device.serialNumber === 'string'
  );
}

export interface HardwareComponent {
  name: string;
  version: string;
  serialNumber: string;
}

export type AssemblyComponent = HardwareComponent;
export type DeviceComponent = HardwareComponent;

export interface Assembly {
  name: string;
  id: string;
  version: string;
  serialNumber: string;
  components: AssemblyComponent[];
  imageId?: string;
}

export interface HardwareDevice {
  name: string;
  id: string;
  version: string;
  keyId: string;
  serialNumber: string;
  macAddress: string;
  components: DeviceComponent[];
  imageId?: string;
}

/**
 * @link https://docs.google.com/document/d/1bafY-F8qFMLd0Las8fQFF6H9PTVYFjv01bhV7owqx8E
 */
export interface HardwareDescription {
  fileVersion: string;
  assembly?: Assembly;
  device: HardwareDevice;
}

/**
 * Retrieves the ID from the assembly if available; otherwise, falls back to the device ID.
 *
 * @example
 * "9002"
 */
export function getIdFromHardwareDescription(hw: HardwareDescription): string {
  return hw.assembly ? hw.assembly.id : hw.device.id;
}

/**
 * Constructs the API ID from the hardware description file.
 * The API ID is a combination of the assembly or device ID, version, and optionally a Key ID, which is used for encrypting firmware binaries.
 * Note: In the "Hardware Description File Format" document, the API ID is now referred to as FWID for clarity.
 * @param hd The hardware description object.
 * @param skipKeyId If true, assembles the API ID without including the Key ID.
 * @returns An API identifier, such as "9600-02" or "9600-02-2423".
 */
export function getApiIdentifierFromHardwareDescription(hd: HardwareDescription, skipKeyId = false): string {
  let apiId = hd.assembly ? `${hd.assembly.id}-${hd.assembly.version}` : `${hd.device.id}-${hd.device.version}`;

  if (!skipKeyId && hd.device.keyId) {
    apiId += `-${hd.device.keyId}`;
  }

  return apiId;
}

export function getNameFromHardwareDescription(hd: HardwareDescription): string {
  return hd.assembly ? hd.assembly.name : hd.device.name;
}

export function getSerialNumberFromHardwareDescription(hd: HardwareDescription): string {
  return hd.assembly ? hd.assembly.serialNumber : hd.device.serialNumber;
}

export function getAllComponentsFromHardwareDescription(hd: HardwareDescription): HardwareComponent[] {
  return hd.assembly ? [...hd.assembly.components, ...hd.device.components] : hd.device.components;
}

export function getImageIdFromHardwareDescription(hd: HardwareDescription): string | undefined {
  return hd.assembly ? hd.assembly.imageId : hd.device.imageId;
}

/**
 * Verifies if two sets of device components are identical.
 * Both sets must contain the same number of components, and each component must match by name and version.
 */
export function deviceComponentsMatch(a: DeviceComponent[], b: DeviceComponent[]): boolean {
  if (a.length === 0 && b.length === 0) {
    return false; // edge case: when both components are empty they don't match since there are no components to compare
  }

  const ac = a.map((c) => `${c.name}-${c.version[0]}`.replace(/\s/g, '').toLocaleLowerCase()).sort();
  const bc = b.map((c) => `${c.name}-${c.version[0]}`.replace(/\s/g, '').toLocaleLowerCase()).sort();

  return isEqual(ac, bc);
}

/**
 * This template is to be used in cases where hardware description file is not available on a device.
 * It is imperative to replace the serial number with a unique identifier.
 */
export const dummyHardwareDescriptionFileContentTemplate: HardwareDescription = {
  fileVersion: '0.0.0',
  device: {
    macAddress: '',
    keyId: '0000',
    serialNumber: '0000-00-0000000-0000',
    name: 'SOMANET Device',
    id: '0000',
    version: '00',
    components: [],
  },
};

export const synapticonSerialNumberRegExp = /^(\d{4})-(\d{2})-(\d+)-(\d{4})$/;

/**
 * Represents a structured serial number.
 *
 * A serial number follows the format: `productId-revision-consecutiveNumber-productionWeek`.
 * Example: `8506-01-040595-4023`
 *  - `8506` → Product ID
 *  - `01`   → Revision
 *  - `040595` → Consecutive Number
 *  - `4023` → Production Week
 */
export interface SerialNumber {
  /**
   * Unique consecutive number assigned to the product.
   * Example: `040595`
   */
  consecutiveNumber: string;

  /**
   * Identifier for the product model or type.
   * Example: `8506`
   */
  productId: string;

  /**
   * Week and year of production.
   * Example: `4023` (Week 40 of the year 2023)
   */
  productionWeek: string;

  /**
   * Revision number indicating product iteration or version.
   * Example: `01`
   */
  revision: string;

  /**
   * Combined product identifier including revision.
   * Example: `8506-01`
   */
  versionedProductId: string;
}

/**
 * Parses a serial number and returns an instance of {@link SerialNumber}.
 *
 * @throws {Error} Throws an error if the provided serial number does not match the expected pattern.
 */
export function parseSerialNumber(serialNumber: string): SerialNumber {
  if (!synapticonSerialNumberRegExp.test(serialNumber)) {
    throw new Error('The provided string is not a valid serial number.');
  }

  const [productId, revision, consecutiveNumber, productionWeek] = serialNumber.split('-');
  const versionedProductId = `${productId}-${revision}`;

  return { consecutiveNumber, productId, productionWeek, revision, versionedProductId };
}

export interface Board {
  description?: string;
  serial_number?: string;
  device_serial_number?: string;
  revision?: string;
  version?: string;
}

export interface StackInfo {
  mac_address: number;
  stack_serial_number: string;
  boards: Board[];
}

/**
 * Extracts device components from the provided Stack Info.
 * The Stack Info typically includes three components: Com, Core, and Drive, each with a name, serial number, and version.
 */
export function extractDeviceComponentsFromStackInfo(stackInfo: StackInfo): DeviceComponent[] {
  return stackInfo.boards.map(
    (board): DeviceComponent => ({
      name: board.description ?? '',
      serialNumber: board.serial_number ?? '',
      version: (board.revision || board.version) ?? '',
    })
  );
}

/**
 * Generates a device serial number from a given address.
 * The address is formatted into a string in the form `XXXX-XX-XXXX000-0000`.
 *
 * @param address - The device address as a number, which will be used to create the serial number.
 * @returns A string representing the formatted device serial number.
 *
 * @example
 * const address = 95010100014791843;
 * const serialNumber = makeDeviceSerialNumberFromAddress(address);
 * console.log(serialNumber); // "9501-01-00014790000-0000"
 */
export function makeDeviceSerialNumberFromAddress(address: number): string {
  const addr = address.toString();
  return addr.slice(0, 4) + '-' + addr.slice(4, 6) + '-' + addr.slice(6, 10) + '000-0000';
}

/**
 * Extracts product IDs and versioned product IDs from a given source.
 *
 * This function handles three types of input:
 * - If the source is a string, it parses the serial number to extract both `productId` and `versionedProductId`.
 * - If the source is a `HardwareDescription`, it extracts the `productId`, `versionedProductId`, `deviceProductId`,
 *   and `versionedDeviceProductId` from either the device or assembly information.
 * - If the source is a `StackInfo`, it parses the `stack_serial_number` to extract the `productId` and `versionedProductId`.
 *
 * @param source - The source from which to extract the product information. It can be a string,
 *                 `StackInfo`, or `HardwareDescription`.
 * @returns An object containing the following properties:
 *  - `deviceProductId`: The ID of the device.
 *  - `productId`: The general product ID.
 *  - `versionedDeviceProductId`: The device ID with version.
 *  - `versionedProductId`: The general product ID with version.
 *
 * @throws {Error} Throws an error if the `source` is of an invalid type.
 */
export function extractProductId(source: string | StackInfo | HardwareDescription): {
  deviceProductId: string;
  productId: string;
  versionedDeviceProductId: string;
  versionedProductId: string;
} {
  if (typeof source === 'string') {
    const { productId, versionedProductId } = parseSerialNumber(source);
    return { deviceProductId: productId, productId, versionedDeviceProductId: versionedProductId, versionedProductId };
  }

  if (isHardwareDescription(source)) {
    const productId = source.assembly ? source.assembly.id : source.device.id;
    const versionedProductId = source.assembly
      ? `${source.assembly.id}-${source.assembly.version}`
      : `${source.device.id}-${source.device.version}`;
    const deviceProductId = source.device.id;
    const versionedDeviceProductId = `${source.device.id}-${source.device.version}`;
    return { deviceProductId, productId, versionedDeviceProductId, versionedProductId };
  }

  if (isStackInfo(source)) {
    const { productId, versionedProductId } = parseSerialNumber(source.stack_serial_number);
    return { deviceProductId: productId, productId, versionedDeviceProductId: versionedProductId, versionedProductId };
  }

  throw new Error('Invalid source type. Expected string, StackInfo, or HardwareDescription.');
}
